import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Switch,
  Alert,
  Skeleton,
  Snackbar,
  Chip,
  Paper,
  alpha,
} from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import { getAdminPayinGateways, setAdminPayinGateways } from "../api";

/* Friendly labels / branding per payin provider. SLPE may surface several
   sub-gateways from the backend — we collapse them into a single provider row. */
const PROVIDER_META = {
  paytm: {
    label: "Paytm",
    tagline: "UPI, cards, net banking & Paytm wallet",
    icon: <BoltRoundedIcon />,
    color: "#002E6E",
  },
  cashfree: {
    label: "Cashfree",
    tagline: "UPI, cards, net banking & wallets",
    icon: <BoltRoundedIcon />,
    color: "#6933FF",
  },
  slpe: {
    label: "SLPE",
    tagline: "UPI, cards & net banking",
    icon: <AccountBalanceRoundedIcon />,
    color: "#6c5ce7",
  },
};

const metaFor = (provider) =>
  PROVIDER_META[provider] || {
    label: String(provider || "").toUpperCase() || "Unknown",
    tagline: "Payment gateway",
    icon: <AccountBalanceRoundedIcon />,
    color: "#90a4ae",
  };

export default function AdminGateways() {
  const [providers, setProviders] = useState([]);
  const [enabledMap, setEnabledMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingProvider, setSavingProvider] = useState(null);
  const [snack, setSnack] = useState(null);

  /* Collapse the raw gateway list into one row per provider (de-duped) and
     derive each provider's on/off state from the returned `disabled` array. */
  const applyData = useCallback((data) => {
    const seen = new Set();
    const list = [];
    (data.gateways || []).forEach((g) => {
      if (!g.provider || seen.has(g.provider)) return;
      seen.add(g.provider);
      list.push({ provider: g.provider, supportsPayin: g.supportsPayin });
    });
    const disabled = new Set(data.disabled || []);
    const map = {};
    list.forEach((p) => {
      map[p.provider] = !disabled.has(p.provider);
    });
    setProviders(list);
    setEnabledMap(map);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getAdminPayinGateways()
      .then(({ data }) => applyData(data))
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load payment gateways"),
      )
      .finally(() => setLoading(false));
  }, [applyData]);

  const handleToggle = async (provider) => {
    const meta = metaFor(provider);
    const nextMap = { ...enabledMap, [provider]: !enabledMap[provider] };
    const disabled = providers
      .filter((p) => !nextMap[p.provider])
      .map((p) => p.provider);

    /* Optimistic flip — revert if the save fails. */
    setEnabledMap(nextMap);
    setSavingProvider(provider);
    setError(null);
    try {
      const { data } = await setAdminPayinGateways(disabled);
      const savedDisabled = new Set(data.disabled || disabled);
      setEnabledMap((prev) => {
        const merged = { ...prev };
        providers.forEach((p) => {
          merged[p.provider] = !savedDisabled.has(p.provider);
        });
        return merged;
      });
      setSnack(
        `${meta.label} ${nextMap[provider] ? "enabled" : "disabled"}`,
      );
    } catch (e) {
      setEnabledMap(enabledMap);
      setError(e.response?.data?.error || "Failed to update payment gateways");
    }
    setSavingProvider(null);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>
        Payment Gateways
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Turn payin gateways on or off. Disabled gateways stop appearing in the
        customer Add Money flow.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3, borderRadius: 3 }}>
        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={72} />
            <Skeleton variant="rounded" height={72} />
            <Skeleton variant="rounded" height={72} />
          </Stack>
        ) : providers.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No payin gateways are configured.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {providers.map((p) => {
              const meta = metaFor(p.provider);
              const on = !!enabledMap[p.provider];
              return (
                <Paper
                  key={p.provider}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderRadius: 2.5,
                    borderColor: on ? alpha(meta.color, 0.4) : "divider",
                    bgcolor: on ? alpha(meta.color, 0.04) : "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: meta.color,
                      bgcolor: alpha(meta.color, 0.14),
                      flexShrink: 0,
                    }}
                  >
                    {meta.icon}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={700} fontSize={15} noWrap>
                        {meta.label}
                      </Typography>
                      <Chip
                        size="small"
                        label={on ? "Enabled" : "Disabled"}
                        color={on ? "success" : "default"}
                        variant={on ? "filled" : "outlined"}
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {meta.tagline}
                    </Typography>
                  </Box>
                  <Switch
                    checked={on}
                    disabled={savingProvider === p.provider}
                    onChange={() => handleToggle(p.provider)}
                    color="success"
                    inputProps={{ "aria-label": `Toggle ${meta.label}` }}
                  />
                </Paper>
              );
            })}
          </Stack>
        )}
      </Card>

      <Stack direction="row" spacing={0.75} alignItems="center" mt={2} sx={{ color: "text.secondary" }}>
        <PowerSettingsNewRoundedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          Changes take effect immediately for all users.
        </Typography>
      </Stack>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ borderRadius: 2 }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
}
